import React, { forwardRef } from 'react'

interface Props {
  // 'radio' for D1/D3 (single-select), 'checkbox' for D2's items. Also picks the glyph, which
  // must always agree with the role.
  role: 'radio' | 'checkbox'
  isSelected: boolean
  // Variant class on top of the shared .resolutionOption styling (refund_resolution_modal.scss).
  className?: string
  testId: string
  tabIndex: number
  // Both required, not optional: the row is a plain div, so one without a keyboard handler is
  // mouse-only. Nor is onClick redundant — a div synthesises no click from Enter/Space.
  onClick: () => void
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void
  children: React.ReactNode
}

// One full-row option on the recovery screens (D1/D2/D3). The whole row is the selection target and
// the glyph is decorative — the row's role and aria-checked carry the state for assistive tech.
// Forwards its ref so useRovingRadioGroup can move focus between rows.
export const RecoveryChoiceRow = forwardRef<HTMLDivElement, Props>(
  (
    {
      role,
      isSelected,
      className = '',
      testId,
      tabIndex,
      onClick,
      onKeyDown,
      children
    },
    ref
  ) => {
    const rowClass = `resolutionOption ${className} ${
      isSelected ? 'selected' : ''
    }`
    const body = <span className='optionBody'>{children}</span>

    // Written out per row kind rather than one div with role={role} because a11y analysers only
    // read what is literally on the tag: a dynamic role reads as a plain div with a click handler
    // (jsx-a11y/no-static-element-interactions), aria-checked behind a spread as a role missing its
    // required state, and spread handlers as mouse-only — the Sonar S1082 finding this row was
    // extracted to fix. Spelling them out keeps the contract machine-checkable.
    if (role === 'checkbox') {
      return (
        <div
          ref={ref}
          role='checkbox'
          aria-checked={isSelected}
          tabIndex={tabIndex}
          data-testid={testId}
          className={rowClass}
          onClick={onClick}
          onKeyDown={onKeyDown}
        >
          <span className='checkGlyph checkGlyphLeading' aria-hidden='true' />
          {body}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        role='radio'
        aria-checked={isSelected}
        tabIndex={tabIndex}
        data-testid={testId}
        className={rowClass}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        <span className='radioGlyph radioGlyphLeading' aria-hidden='true' />
        {body}
      </div>
    )
  }
)

RecoveryChoiceRow.displayName = 'RecoveryChoiceRow'

export default RecoveryChoiceRow
